import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import { ArrowLeft, Clock, Eye, Tag } from 'lucide-react';
import { useSEO } from '../utils/seo';
import ShareButtons from '../components/ShareButtons';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useSEO({
    title: post ? post.title : 'Blog Post',
    description: post ? post.excerpt : '',
    canonical: post ? `https://shopperden.com/blog/${post.slug}` : undefined,
    type: 'article',
    image: post?.cover_image || undefined,
    schema: post ? {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": post.title,
      "description": post.excerpt,
      "url": `https://shopperden.com/blog/${post.slug}`,
      "image": post.cover_image || "",
      "datePublished": post.created_at,
      "dateModified": post.updated_at,
      "publisher": {
        "@type": "Organization",
        "name": "Shopperden Ltd",
        "url": "https://shopperden.com"
      },
      "author": {
        "@type": "Organization",
        "name": "Shopperden"
      }
    } : undefined
  });

  const fetchPost = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/blog/posts/${slug}`);
      setPost(res.data);
    } catch {
      setPost(null);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchPost();
  }, [slug, fetchPost]);

  if (loading) return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <div className="flex justify-center py-32"><div className="w-8 h-8 border-2 border-[#E07A3A] border-t-transparent rounded-full animate-spin" /></div>
    </div>
  );

  if (!post) return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <h1 className="font-['Playfair_Display'] text-3xl text-[#1A1A1A] mb-4">Post Not Found</h1>
        <Link to="/blog" className="text-[#E07A3A] underline">Back to Blog</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="blog-post-page">
      <Header />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/blog" className="flex items-center gap-2 text-sm text-slate-500 hover:text-[#E07A3A] mb-8 transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back to Blog
        </Link>

        {post.cover_image && (
          <div className="w-full h-64 sm:h-96 overflow-hidden mb-8">
            <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
          </div>
        )}

        <div className="flex items-center gap-3 mb-4">
          {post.category && (
            <span className="text-[10px] tracking-wider uppercase font-bold text-[#E07A3A] bg-[#E07A3A]/10 px-2 py-1">{post.category.replace(/-/g, ' ')}</span>
          )}
          <span className="flex items-center gap-1 text-xs text-slate-400">
            <Clock className="h-3 w-3" />
            {new Date(post.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
          </span>
          {post.views > 0 && (
            <span className="flex items-center gap-1 text-xs text-slate-400">
              <Eye className="h-3 w-3" />{post.views} views
            </span>
          )}
        </div>

        <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl text-[#1A1A1A] font-semibold mb-6">{post.title}</h1>

        {/* Top share row — visible immediately so readers can share before
            reading the article (drives more organic traffic). */}
        <div className="mb-8 pb-6 border-b border-slate-100">
          <ShareButtons
            url={`https://shopperden.com/blog/${post.slug}`}
            title={post.title}
            dataTestId="blog-share-top"
            size="md"
          />
        </div>

        {post.excerpt && (
          <p className="text-lg text-slate-500 leading-relaxed mb-8 border-l-4 border-[#E07A3A] pl-4 italic">{post.excerpt}</p>
        )}

        <div
          className="prose prose-slate max-w-none text-slate-700 leading-relaxed text-base"
          data-testid="blog-content"
          // Blog posts are admin-authored; we render the stored HTML directly so
          // semantic <h2>/<h3>/<ul>/<a> tags reach search engines instead of
          // being flattened into a pre-wrap text block.
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        {post.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-12 pt-6 border-t border-slate-200">
            <Tag className="h-4 w-4 text-slate-400" />
            {post.tags.map(tag => (
              <span key={tag} className="text-xs bg-slate-100 text-slate-500 px-3 py-1">{tag}</span>
            ))}
          </div>
        )}

        <div className="mt-12 pt-8 border-t border-slate-200">
          {/* Bottom share row — captures readers who finished the article. */}
          <div className="mb-8">
            <ShareButtons
              url={`https://shopperden.com/blog/${post.slug}`}
              title={post.title}
              dataTestId="blog-share-bottom"
              label="Liked this? Share it"
            />
          </div>
          <div className="text-center">
            <p className="text-slate-400 text-sm">Published by Shopperden Ltd</p>
            <Link to="/properties" className="text-[#E07A3A] text-sm font-medium hover:underline mt-2 inline-block">
              Browse Properties
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPostPage;
